import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { createApiCall } from 'services';
import { CertificateScanHistoryData } from 'store/certificates/model';
import { useSelector } from 'store/hooks';

export type ScanLog = {
  id: string;
  certificate_id: string;
  type: string;
  user_type: 'customer' | 'anonymous';
  user_id: string | null;
  created_at: string;
  user?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    email: string;
    avatar: string | null;
  };
  metadata?: {
    location?: string;
    city?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
  };
  certificate_status?: string;
  certificate_public_url?: string;
  certificate_display_status?: string;
};

type ScanLogsResponse = {
  data: ScanLog[];
  total: number;
  limit: number;
  offset: number;
};

type UseScanLogsParams = {
  limit?: number;
  offset?: number;
  sort?: 'ASC' | 'DESC';
  enabled?: boolean;
  certificateId?: string;
};

const getScanLogs = async (certificateId: string, params: UseScanLogsParams): Promise<CertificateScanHistoryData> => {
  const response = await createApiCall({
    method: 'GET',
    url: `scan-log/public/${certificateId}`,
    params: {
      limit: params.limit || 20,
      offset: params.offset || 0,
      sort: params.sort || 'DESC',
    },
  });

  return response.data as CertificateScanHistoryData;
};

/**
 * Serve a narrower window out of a WIDER one already in the cache.
 *
 * `limit` is part of the query key, so asking for 20 scans is a different query
 * from asking for 500 — a cache MISS even though the 500 already contains every
 * row the 20 needs. That is not hypothetical: the certificate page fetches 500
 * (it feeds the invalid-scan banner and the map), and opening the Scans screen
 * then asked for 20 and waited out a full round-trip for data sitting in memory.
 * The screen therefore rendered its header, and the list dropped in afterwards.
 *
 * The API returns the top N by date for `limit: N, offset: 0`, so a wider window
 * is a strict superset of a narrower one and slicing it is exact, not an
 * approximation. Guarded accordingly: page 0 only, matching sort only, and only
 * when the cached window actually holds enough rows (or has reached the end).
 *
 * `placeholderData`, deliberately, not `initialData`: the slice never enters the
 * cache under this key and the real request still goes out, so this is purely
 * "show the right answer immediately and confirm it", never a way for a stale
 * slice to persist as if it were a response.
 */
const sliceFromWiderWindow = (
  queryClient: ReturnType<typeof useQueryClient>,
  certificateId: string | undefined,
  params: UseScanLogsParams,
): CertificateScanHistoryData | undefined => {
  if (!certificateId) return undefined;
  if (params.offset) return undefined;

  const wantLimit = params.limit || 20;
  const wantSort = params.sort || 'DESC';

  for (const [key, cached] of queryClient.getQueriesData<CertificateScanHistoryData>({
    queryKey: ['scan-logs', certificateId],
  })) {
    const cachedParams = (key as unknown[])[2] as UseScanLogsParams | undefined;
    if (!cached || !Array.isArray(cached.data)) continue;
    if (cachedParams?.offset) continue;
    if ((cachedParams?.sort || 'DESC') !== wantSort) continue;

    // Enough rows to satisfy the ask, or the cached window already reached the
    // end of the list — in which case a shorter slice is all there is anyway.
    const isComplete = cached.data.length >= (cached.total ?? 0);
    if (cached.data.length < wantLimit && !isComplete) continue;

    return { ...cached, data: cached.data.slice(0, wantLimit), limit: wantLimit };
  }

  return undefined;
};

export const useScanLogs = (params: UseScanLogsParams = {}): UseQueryResult<CertificateScanHistoryData, Error> => {
  const certificateIdFromStore = useSelector(state => state.certificates.certificate?.id);
  const certificateId = params.certificateId || certificateIdFromStore;
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['scan-logs', certificateId, params],
    queryFn: () => getScanLogs(certificateId!, params),
    enabled: params.enabled !== false && !!certificateId,
    placeholderData: () => sliceFromWiderWindow(queryClient, certificateId, params),
  });
};
